import type { } from '../data/apps.json';

interface App {
  id: string;
  name: string;
  tagline: string;
  description: string;
  icon: string;
  color: string;
  platforms: string[];
  pricing: string;
  status: string;
  featured: boolean;
  version: string;
  downloadUrl: string;
  demoUrl: string;
  githubUrl: string;
  docsUrl: string;
  screenshots: string[];
  tags: string[];
}

export function renderApps(): string {
  return /* html */`
<div class="page-wrapper apps-page">
  <div class="page-hero">
    <span class="section-label">Apps</span>
    <h1 class="hero-title anim-fade-up">
      ${L('Software, die du', 'Software you can')}<br/><span class="gradient-text">${L('wirklich nutzen kannst.', 'actually use.')}</span>
    </h1>
    <p class="hero-mission anim-fade-up" style="animation-delay:0.1s">
      ${L(
        'KI-Tools, Produktivitäts-Apps, Spiele und experimentelle Schnittstellen. Jede einzelne gebaut, um ein echtes Problem zu lösen, ohne sich wie ein Kompromiss anzufühlen.',
        'AI tools, productivity apps, games, and experimental interfaces. Each one built to solve a real problem in a way that doesn\'t feel like a compromise.'
      )}
    </p>

    <!-- Filter bar -->
    <div class="filter-bar anim-fade-up" style="animation-delay:0.2s" role="group" aria-label="${L('Apps filtern', 'Filter apps')}">
      <button class="filter-btn active" data-filter="all">${L('Alle', 'All')}</button>
      <button class="filter-btn" data-filter="Active">${L('Aktiv', 'Active')}</button>
      <button class="filter-btn" data-filter="Open Source">Open Source</button>
      <button class="filter-btn" data-filter="AI">AI</button>
      <button class="filter-btn" data-filter="Web">Web</button>
      <button class="filter-btn" data-filter="Linux">Linux</button>
      <button class="filter-btn" data-filter="iOS">iOS</button>
      <button class="filter-btn" data-filter="Prototype">Prototype</button>
    </div>
  </div>

  <div class="page-section">
    <div id="apps-grid" class="apps-grid stagger">
      <!-- Populated by initApps() -->
    </div>
  </div>
</div>
`;
}

export function initApps(): void {
  import('../data/apps.json').then(({ default: appsRaw }) => {
    const apps = appsRaw as App[];
    const grid = document.getElementById('apps-grid');
    if (!grid) return;

    let activeFilter = 'all';

    function render(filter: string): void {
      const filtered = filter === 'all'
        ? apps
        : apps.filter(a =>
            a.status === filter ||
            a.pricing === filter ||
            a.platforms.includes(filter) ||
            a.tags.includes(filter)
          );

      grid!.innerHTML = filtered.map(app => appCard(app)).join('');

      // Bind detail open
      grid!.querySelectorAll('.app-card').forEach(card => {
        card.addEventListener('click', () => {
          const id = (card as HTMLElement).dataset.appId;
          openAppDetail(apps.find(a => a.id === id)!);
        });
      });
    }

    // Filter buttons
    document.querySelectorAll('.filter-btn').forEach(btn => {
      btn.addEventListener('click', () => {
        document.querySelectorAll('.filter-btn').forEach(b => b.classList.remove('active'));
        btn.classList.add('active');
        activeFilter = (btn as HTMLElement).dataset.filter ?? 'all';
        render(activeFilter);
      });
    });

    render('all');
  });
}

function appCard(app: App): string {
  const statusColor: Record<string, string> = {
    'Active': 'schwarz', 'Prototype': 'amber', 'Archived': 'dim', 'Private Beta': 'gold',
  };
  return /* html */`
  <article class="app-card card" data-app-id="${app.id}" tabindex="0" role="button"
           aria-label="View ${app.name}" style="--app-color:${app.color}">
    <div class="app-card-header">
      <div class="app-card-icon" style="background:color-mix(in srgb, ${app.color} 15%, transparent)">
        <span style="font-size:1.6rem">${app.icon}</span>
      </div>
      <div class="app-card-badges">
        <span class="badge badge-${statusColor[app.status] ?? 'dim'}">${app.status}</span>
        <span class="badge badge-${pricingColor(app.pricing)}">${app.pricing}</span>
      </div>
    </div>

    <h3 class="app-card-name">${app.name}</h3>
    <p class="app-card-tagline">${app.tagline}</p>

    <div class="app-card-platforms">
      ${app.platforms.map(p => `<span class="badge badge-dim">${p}</span>`).join('')}
    </div>

    <div class="app-card-links">
      ${app.githubUrl ? `<a href="${app.githubUrl}" class="app-link" target="_blank" rel="noopener" onclick="event.stopPropagation()">GitHub ↗</a>` : ''}
      ${app.demoUrl   ? `<a href="${app.demoUrl}"   class="app-link" target="_blank" rel="noopener" onclick="event.stopPropagation()">Demo ↗</a>` : ''}
      ${app.downloadUrl ? `<a href="${app.downloadUrl}" class="app-link" target="_blank" rel="noopener" onclick="event.stopPropagation()">Download ↗</a>` : ''}
    </div>

    <div class="app-card-glow" style="--glow-color:${app.color}"></div>
  </article>
  `;
}

function openAppDetail(app: App): void {
  const modal = document.createElement('div');
  modal.className = 'modal-overlay';
  modal.setAttribute('role', 'dialog');
  modal.setAttribute('aria-modal', 'true');
  modal.setAttribute('aria-label', app.name);
  modal.innerHTML = /* html */`
  <div class="modal-content app-detail">
    <button class="modal-close" aria-label="Close">×</button>

    <div class="app-detail-header" style="--app-color:${app.color}">
      <span style="font-size:3rem">${app.icon}</span>
      <div>
        <h2 class="app-detail-name">${app.name}</h2>
        <p class="app-detail-tagline">${app.tagline}</p>
        <div class="app-detail-meta">
          <span class="badge badge-${pricingColor(app.pricing)}">${app.pricing}</span>
          <span class="badge badge-dim">v${app.version}</span>
          ${app.platforms.map(p => `<span class="badge badge-dim">${p}</span>`).join('')}
        </div>
      </div>
    </div>

    <p class="app-detail-desc">${app.description}</p>

    <div class="app-detail-tags">
      ${app.tags.map(t => `<span class="badge badge-red">${t}</span>`).join('')}
    </div>

    <div class="app-detail-actions">
      ${app.downloadUrl ? `<a href="${app.downloadUrl}" class="btn btn-primary" target="_blank" rel="noopener">Download</a>` : ''}
      ${app.demoUrl     ? `<a href="${app.demoUrl}"     class="btn btn-secondary" target="_blank" rel="noopener">Live Demo ↗</a>` : ''}
      ${app.githubUrl   ? `<a href="${app.githubUrl}"   class="btn btn-ghost" target="_blank" rel="noopener">GitHub ↗</a>` : ''}
      ${app.docsUrl     ? `<a href="${app.docsUrl}"     class="btn btn-ghost" target="_blank" rel="noopener">Docs ↗</a>` : ''}
      ${!app.downloadUrl && !app.demoUrl ? '<span class="badge badge-dim" style="padding:var(--s2) var(--s4)">Coming Soon</span>' : ''}
    </div>
  </div>
  `;

  modal.addEventListener('click', e => {
    if (e.target === modal) modal.remove();
  });
  modal.querySelector('.modal-close')!.addEventListener('click', () => modal.remove());
  document.body.appendChild(modal);

  // Trap focus
  const close = modal.querySelector('.modal-close') as HTMLElement;
  close.focus();
}

function pricingColor(p: string): string {
  if (p === 'Open Source') return 'schwarz';
  if (p === 'Free') return 'red';
  if (p === 'Experimental') return 'pink';
  if (p === 'Coming Soon') return 'dim';
  return 'amber';
}

import { i18n } from '../lib/i18n.ts';
function L(de: string, en: string): string { return i18n.current === 'de' ? de : en; }
